import {
  provideRouter,
  withComponentInputBinding,
  withDebugTracing,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';

import { runGuardsSequentially } from '@personal-area/common';
import { authGuard } from '@personal-area/global/authorization/services';
import { emailConfirmationGuard } from '@personal-area/global/email-confirmation';
import { extract } from '@personal-area/global/i18n';

import { guestGuard } from './core/guards/guest.guard';
import { LogoutGuard } from './core/guards/logout.guard';
import { canActivateNotFound } from './core/guards/not-found.guard';
import { PreloadingStrategyService } from './core/routing/preloading-strategy.service';
import { MaintenanceGuard } from './maintenance/guards/maintenance.guard';

export const appRouting = provideRouter(
  [
    {
      path: 'registration',
      loadChildren: () =>
        import('./register/register.module').then((m) => m.RegisterModule),
      canMatch: [MaintenanceGuard],
      canActivate: [guestGuard],
      data: { preload: true },
    },
    {
      path: 'registration/account',
      loadChildren: () =>
        import('./register/pages/account/account.module').then(
          (m) => m.AccountModule,
        ),
      data: {
        preload: true,
        isHeaderLocked: true,
        isHeaderNavigationLocked: true,
      },
      canMatch: [MaintenanceGuard],
      canActivate: [runGuardsSequentially([authGuard, emailConfirmationGuard])],
    },
    {
      path: 'login',
      loadChildren: () =>
        import('./login/login.module').then((m) => m.LoginModule),
      data: { preload: true },
      canMatch: [MaintenanceGuard],
      canActivate: [guestGuard],
    },
    {
      path: 'recovery',
      loadChildren: () =>
        import('./recovery/recovery.module').then((m) => m.RecoveryModule),
      canMatch: [MaintenanceGuard],
      canActivate: [guestGuard],
    },
    {
      path: 'email-confirmation',
      loadChildren: () =>
        import('./email-confirmation/email-confirmation.module').then(
          (m) => m.EmailConfirmationModule,
        ),
      data: {
        preload: true,
        isHeaderLocked: true,
        isHeaderNavigationLocked: true,
      },
      canMatch: [MaintenanceGuard],
    },
    {
      path: 'partner',
      loadChildren: () =>
        import('./partner/app-partner-routing.module').then(
          (m) => m.AppPartnerRoutingModule,
        ),
      canMatch: [MaintenanceGuard],
    },
    {
      path: 'logout',
      children: [],
      canActivate: [LogoutGuard],
    },
    {
      path: 'maintenance',
      loadChildren: () =>
        import('./maintenance/maintenance.module').then(
          (m) => m.MaintenanceModule,
        ),
      canMatch: [MaintenanceGuard],
    },
    {
      path: 'redirect',
      loadChildren: () =>
        import('./redirect/redirect.module').then((m) => m.RedirectModule),
    },
    {
      path: '404',
      loadComponent: () =>
        import('./shared/page-not-found/page-not-found.component').then(
          (m) => m.PageNotFoundComponent,
        ),
      canActivate: [canActivateNotFound],
      data: {
        noCache: true,
        title: extract('page-not-found.title'),
      },
    },
    {
      // WARN: must be BEFORE empty path ('') shell module route
      path: '',
      redirectTo: '/dashboard',
      pathMatch: 'full',
    },
    {
      // WARN: must be AFTER empty path ('') redirect route
      path: '',
      loadChildren: () =>
        import('./shell/shell.module').then((m) => m.ShellModule),
      data: { preload: true },
      canMatch: [MaintenanceGuard],
    },
  ],
  withPreloading(PreloadingStrategyService),
  withInMemoryScrolling({ scrollPositionRestoration: 'top' }),
  withDebugTracing(),
  withEnabledBlockingInitialNavigation(),
  withComponentInputBinding(),
);
